import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import { useProfileField } from "@/hooks/useHealthProfile";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { IDeviceImplant, MedicalDetailType } from "@/types/health-profile";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import { useTranslation } from "react-i18next";
import { formattedYear } from "@/utils/date-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MLoader from "@/components/ui/atoms/MLoader";
import { trackUserDataLoaded } from "@/utils/gtm";
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from "@/constants/analytics.constants";

const ImplantedDevicesForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode") as "add" | "edit";
  const editIndex = params.get("index")
    ? Number(params.get("index"))
    : undefined;
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { showToast } = useToast();
  const [customDeviceName, setCustomDeviceName] = useState("");
  const [customImplantDate, setCustomImplantDate] = useState("");
  const [showDeviceDrawer, setShowDeviceDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isOther, setIsOther] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [oCustomDeviceName, setOCustomDeviceName] = useState("");
  const [oCustomImplantDate, setOCustomImplantDate] = useState("");

  // Use correct typeahead category (dynamic per member type)
  const category = `implanted_devices_${memberInfo.memberType}`;
  const { values: typeAheadOptions, fetch: fetchTypeAhead } =
    useTypeAhead(category);
  const { fieldValue: deviceImplants = [], saveField: saveDeviceImplants } =
    useProfileField<IDeviceImplant[]>(
      memberId,
      MedicalDetailType.DEVICE_IMPLANTS,
      []
    );

  useEffect(() => {
    if (
      mode === "edit" &&
      editIndex !== undefined &&
      deviceImplants[editIndex]
    ) {
      const deviceImplant = deviceImplants[editIndex];
      const name = deviceImplant.name;
      const found = typeAheadOptions.includes(name);
      if (found) {
        setCustomDeviceName(name);
      } else {
        setCustomDeviceName(t("common.other"));
        setIsOther(true);
        setOtherValue(name);
      }
      setOCustomDeviceName(name);
      setOCustomImplantDate(deviceImplant.date);
      setCustomImplantDate(deviceImplant.date);
    }
    if (mode === "add") {
      setCustomDeviceName("");
      setCustomImplantDate("");
    }
  }, [mode, editIndex, deviceImplants]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_ADD,
    });
  }, []);

  const sort = (devices: IDeviceImplant[]) => {
    return devices.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let updated: IDeviceImplant[];
      const finalDeviceName = isOther ? otherValue : customDeviceName;
      if (mode === "edit" && editIndex !== undefined) {
        updated = [...deviceImplants];
        updated[editIndex] = {
          name: finalDeviceName,
          date: customImplantDate,
        };
      } else {
        updated = [
          ...deviceImplants,
          { name: finalDeviceName, date: customImplantDate },
        ];
      }
      await saveDeviceImplants(sort(updated));
      
      // Track completion
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_ADD_COMPLETE,
      });

      navigate("/profile/health-profile/implant-devices");
    } catch (e) {
      showToast({
        message: t("add_implanted_device.saveFailed"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const getTypeAheadOptions = () => {
    const options = typeAheadOptions.map((option) => ({
      value: option,
      label: option,
    }));
    options.push({ value: t("common.other"), label: t("common.other") });
    return options;
  };

  const handleDeviceSelect = (val: string) => {
    const other = t("common.other");
    if (val === other) {
      setShowDeviceDrawer(false);
      setOtherValue("");
      setIsOther(true);
      setCustomDeviceName(other);
    } else {
      setIsOther(false);
      setCustomDeviceName(val);
      setOtherValue("");
      setShowDeviceDrawer(false);
    }
  };

  const checkIsValid = () => {
    if (isOther && otherValue.trim().length === 0) {
      return false;
    } else if (customDeviceName.trim().length === 0) {
      return false;
    }
    if (customImplantDate.trim().length === 0) {
      return false;
    }
    if (saving) {
      return false;
    }
    if (mode === "edit") {
      const deviceName = isOther ? otherValue : customDeviceName;
      const isDeviceChanged = deviceName !== oCustomDeviceName;
      const isDateChanged = customImplantDate !== oCustomImplantDate;
      return isDeviceChanged || isDateChanged;
    }
    return true;
  };

  if (saving) return <MLoader />;

  return (
    <div className="profile-page-root">
      <FormHeader module="add_implanted_device" memberInfo={memberInfo} />
      <MSpacer />
      <div className="profile-input-container">
        <MInputField
          type="text"
          label={t("add_implanted_device.deviceName")}
          value={customDeviceName}
          placeholder={t("common.enterOrChoose")}
          showIcon
          icon="caret-down-filled"
          onClick={() => setShowDeviceDrawer(true)}
        />
        {isOther && (
          <MInputField
            label={t("add_implanted_device.deviceNameLabel")}
            type="text"
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
            placeholder={t("common.notAnswered")}
          />
        )}
        <MDateInputField
          label={t("add_implanted_device.deviceDate")}
          showYearOnly
          minYear={memberInfo.dobYear || 1940}
          value={customImplantDate ? new Date(customImplantDate) : undefined}
          onDateChange={(date) => setCustomImplantDate(date.toISOString())}
          formatDate={(date) => (date ? formattedYear(date) : "")}
          placeholder={t("common.notAnswered")}
          datePickerTitle={t("common.selectYear")}
        />
      </div>
      <MDropDownDrawer
        open={showDeviceDrawer}
        onClose={() => setShowDeviceDrawer(false)}
        title={t("add_implanted_device.selectDevice")}
        items={getTypeAheadOptions()}
        initialValue={customDeviceName}
        onSelect={handleDeviceSelect}
        variant="single"
      />

      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={!checkIsValid()}
          onClick={handleSave}
          text={t("common.done")}
        />
      </div>
    </div>
  );
};

export default ImplantedDevicesForm;
